/**
 * O CSS da folha impressa: as caixas de margem do `@page`.
 *
 * O motor decide **o que** vai em cada caixa (`molduraDaFolha`, em
 * `@miolos/core`); aqui só se escreve isso em CSS. As caixas de margem
 * (`@top-center`, `@bottom-right`...) vivem **na margem** da folha, fora da
 * mancha: é por isso que o cabeçalho não empurra uma linha de texto e se repete
 * sozinho em todas as folhas, sem nenhum truque de posicionamento.
 *
 * O que já foi medido no navegador que imprime (Chromium, o mesmo do PDF do
 * servidor) — e está aqui porque cada um desses pontos custou uma medição:
 *
 * - As caixas de margem **funcionam** e `counter(page)` dá o número certo.
 * - `@page :left` e `@page :right` funcionam: é assim que o número alterna de
 *   lado (pé de fora, como livro). Como toda folha é par ou ímpar, a regra da
 *   paridade traz **as seis caixas de novo** — se trouxesse só o número, o
 *   cabeçalho desapareceria de todas as folhas.
 * - `@page <nome>` funciona e é o que limpa a faixa nas folhas de abertura; a
 *   regra nomeada precisa repetir `size` e `margin`, senão a folha perde as
 *   margens que a norma pede.
 * - A caixa do **centro ocupa a largura toda**, e as laterais se encostam nela: o
 *   filete desenhado nas duas laterais se encontra no meio e a linha atravessa a
 *   mancha inteira — é assim que o cabeçalho em duas pontas ganha o filete.
 * - Caixa com `content: none` **não é desenhada** — e com ela iria a borda.
 *   Medido: caixa vazia com `content: ""`, com espaço, com altura declarada, com
 *   espaço não separável e com glifo transparente **não** desenha a borda. Por
 *   isso o filete só vai nas caixas que têm texto de cabeçalho (e não na do
 *   número, que é do alto mas não é cabeçalho).
 * - Imagem dentro da caixa (`content: url()`) **não desenha**: a imagem de
 *   cabeçalho vai como **fundo** da caixa de margem do alto (ver `fundoDaImagem`
 *   em `cssDaFolha`) — e é o único jeito de desenhar acima da mancha em toda
 *   folha: elemento `position: fixed` não se repete e conteúdo é cortado na
 *   mancha.
 * - `counter-reset: page` e `break-before: right` são ignorados: a contagem não
 *   recomeça e o capítulo não é forçado para a página da direita.
 */

import {
  ELEMENTOS_DOCUMENTO,
  GRUPO_DE_ELEMENTO_DOCUMENTO,
  MARGEM_DA_ANOTACAO_MM,
  PAGINA_DA_ANOTACAO,
  PAGINA_DE_ABERTURA,
  geometriaDoPapel,
  molduraDaFolha,
  fonteDoDocumento,
  papelDoDocumento,
} from '@miolos/core';
import type { CaixaDaFolha, DocumentoAcademico, FaixasDaFolha, MolduraDaFolha } from '@miolos/core';
import type { CSSProperties } from 'react';

/** A letra com serifa — a do modelo da norma. */
const FAMILIA_SERIFADA = '"Times New Roman", Times, Georgia, serif';

/** A letra sem serifa. */
const FAMILIA_SEM_SERIFA = 'Arial, Helvetica, sans-serif';

/**
 * A letra do documento, que é a **mesma da mancha**: a faixa não pode parecer de outro livro.
 *
 * Quem escolhe é a pessoa (Times New Roman ou Arial), e a escolha atravessa a mancha, as
 * faixas da folha e o editor — é o que faz a tela mostrar o que vai sair no papel.
 */
export function familiaDoDocumento(documento: Pick<DocumentoAcademico, 'fonte'>): string {
  return fonteDoDocumento(documento) === 'ARIAL' ? FAMILIA_SEM_SERIFA : FAMILIA_SERIFADA;
}

/** A família de uma faixa: a do documento, ou a que a pessoa fixou só naquela faixa. */
function familiaDaFaixa(estilo: { fonte?: string }, documento: DocumentoAcademico): string {
  if (estilo.fonte === 'SERIFADA') return FAMILIA_SERIFADA;
  if (estilo.fonte === 'SEM_SERIFA') return FAMILIA_SEM_SERIFA;
  return familiaDoDocumento(documento);
}

const COR_DO_FILETE = '#999999';
const ESPESSURA_DO_FILETE_MM = 0.2;

/**
 * Quanto de folga a margem de cima precisa quando há imagem de cabeçalho.
 *
 * A imagem entra a 6 mm da borda e a faixa de texto ocupa os últimos milímetros
 * da margem; sem folga, o filete do cabeçalho cairia em cima da imagem.
 */
const FOLGA_DA_IMAGEM_MM = 18;

/** Margem de cima mínima quando há faixa de texto ou número no alto. */
const MINIMO_PARA_FAIXA_MM = 16;

export interface CssDaFolha {
  /** As regras de `@page` (folha, margens e faixas) — só valem na impressão. */
  pagina: string;
  /** A margem de cima usada na impressão, em milímetros. */
  margemDoTopoMm: number;
  /** A folha resolvida pelo motor, para a prévia mostrar o que vai sair. */
  moldura: MolduraDaFolha;
}

/**
 * O CSS de impressão do documento: `@page` com folha, margens e faixas.
 *
 * Devolve também a margem de cima calculada, porque a imagem de cabeçalho é
 * presa dentro dela (`top: -(margem - 6) mm`) e quem desenha precisa do mesmo
 * número — se os dois divergirem, a imagem cobre a primeira linha do texto.
 */
export function cssDaFolha(documento: DocumentoAcademico): CssDaFolha {
  const papel = papelDoDocumento(documento);
  const geometria = geometriaDoPapel(papel);
  const moldura = molduraDaFolha(documento);

  const margemDoTopoMm = Math.max(
    geometria.margens.topo,
    documento.cabecalho ? documento.cabecalho.alturaMm + FOLGA_DA_IMAGEM_MM : 0,
    temCaixa(moldura.comum.alto) ? MINIMO_PARA_FAIXA_MM : 0,
  );

  const margem = `${margemDoTopoMm}mm ${geometria.margens.direita}mm ${geometria.margens.fundo}mm ${geometria.margens.esquerda}mm`;
  const folhaBase = `size: ${papel} portrait; margin: ${margem};`;

  /*
   * A **imagem do cabeçalho** é o **fundo da caixa de margem do alto** do `@page`.
   *
   * Não é elemento `position: fixed` nem `content: url(...)`. Medido no PDF desta
   * versão do Chromium: o elemento fixo **não se repete** — sai pintado uma vez, no pé
   * de uma folha (faixa de 247 a 265 mm na A4), que é o defeito que a pessoa vê; e
   * conteúdo é **cortado na mancha**: nada acima da margem de cima sai impresso, nem
   * com margem negativa. `content: url(...)` também não desenha (medido antes).
   *
   * O que desenha, em **toda** folha e por cima da mancha, é o **fundo** da caixa de
   * margem: a caixa do centro ocupa a largura da mancha, e a imagem sai na altura
   * escolhida (sem distorcer, centrada) a 6 mm da borda de cima do papel.
   */
  const fundoDaImagem = documento.cabecalho
    ? ` background-image: url("${String(documento.cabecalho.url).replace(/"/g, '%22')}");` +
      ' background-repeat: no-repeat;' +
      ' background-position: center 6mm;' +
      ` background-size: auto ${documento.cabecalho.alturaMm}mm;`
    : '';

  const regras = [
    `@page { ${folhaBase} ${caixas(moldura.comum, moldura, true, documento, fundoDaImagem)} }`,
    /*
     * As folhas de abertura perdem a faixa **e** o número, mas mantêm a folha e
     * as margens: a norma quer a capa limpa e o texto numerado — e a contagem
     * corre sozinha desde a capa, então o primeiro número impresso é o certo.
     */
    `@page ${PAGINA_DE_ABERTURA} { ${folhaBase} ${caixas(folhaLimpa(), moldura, false, documento, fundoDaImagem)} }`,
    /*
     * A folha de anotação é folha de escrever: **folha inteira**, com a margem
     * estreita da anotação e sem caixa nenhuma (nada de cabeçalho, rodapé ou
     * número). Página nomeada própria porque a margem muda: com as margens da
     * norma, a pauta ficaria só na mancha e metade da folha sairia vazia.
     *
     * Como em toda regra nomeada, `size` e `margin` vêm de novo, e as seis caixas
     * vêm **vazias** — e não ausentes: a folha de anotação também é uma folha par
     * ou ímpar, então continua casando com `@page` e com `@page :left`/`:right`,
     * que pintariam a faixa por cima da pauta.
     */
    `@page ${PAGINA_DA_ANOTACAO} { size: ${papel} portrait; margin: ${MARGEM_DA_ANOTACAO_MM}mm; ${caixas(
      folhaLimpa(),
      moldura,
      false,
      documento,
    )} }`,
  ];

  if (moldura.pares)
    regras.push(
      `@page :left { ${caixas(moldura.pares, moldura, true, documento, fundoDaImagem)} }`,
    );
  if (moldura.impares)
    regras.push(
      `@page :right { ${caixas(moldura.impares, moldura, true, documento, fundoDaImagem)} }`,
    );

  /*
   * A página nomeada das folhas de abertura. Só vale na impressão e obriga uma
   * quebra antes de cada peça nomeada — e todas elas já começam em folha nova.
   */
  const abertura = ELEMENTOS_DOCUMENTO.filter(
    (elemento) => GRUPO_DE_ELEMENTO_DOCUMENTO[elemento] === 'PRE_TEXTUAL',
  )
    .map((elemento) => `.documento-abnt [data-elemento="${elemento}"]`)
    .join(', ');

  regras.push(`@media print { ${abertura} { page: ${PAGINA_DE_ABERTURA}; } }`);

  /*
   * A folha de anotação troca de página nomeada na impressão (folha inteira,
   * margem estreita, sem faixa). A regra vale para **todas** elas: cada folha já
   * começa em folha nova pelas quebras do desenho.
   */
  regras.push(
    `@media print { .documento-abnt [data-elemento="PAGINA_DE_ANOTACAO"] { page: ${PAGINA_DA_ANOTACAO}; } }`,
  );

  return { pagina: regras.join('\n'), margemDoTopoMm, moldura };
}

/** As seis caixas de uma folha (alto e pé, em três posições cada). */
function caixas(
  faixas: FaixasDaFolha,
  moldura: MolduraDaFolha,
  comFilete: boolean,
  documento: DocumentoAcademico,
  /** O fundo da imagem de cabeçalho, que vai na caixa do centro. */
  fundoDaImagem = '',
): string {
  /*
   * A imagem de cabeçalho viaja numa **caixa só**, e na caixa que o navegador
   * desenha: caixa vazia é **descartada** quando alguma outra do alto tem texto
   * (medido: com o número na direita, a caixa vazia do centro não sai). Por isso a
   * preferência é a do **centro** — a que ocupa a largura da mancha, então a imagem
   * sai centrada na folha —, depois a da direita, depois a da esquerda. Sem nenhuma
   * caixa com conteúdo, a do centro nasce com `content: ""` e desenha.
   */
  const comConteudo = (posicao: 'esquerda' | 'centro' | 'direita') =>
    faixas.alto[posicao].tipo !== 'VAZIA';

  const donaDaImagem = fundoDaImagem
    ? (['centro', 'direita', 'esquerda'] as const).find(comConteudo) ?? 'centro'
    : null;

  const alto = (posicao: 'esquerda' | 'centro' | 'direita', nome: string) =>
    caixa(nome, faixas.alto[posicao], {
      fundoDaImagem: posicao === donaDaImagem ? fundoDaImagem : '',
      alto: true,
      /*
       * O filete vai em **toda** caixa do alto que tenha texto de cabeçalho: numa
       * (cabeçalho centrado) ele sai embaixo do texto; em duas (autor de um lado,
       * título do outro) as metades se encontram e a linha atravessa a folha. A
       * caixa do número fica de fora: o filete é do cabeçalho.
       */
      filete: comFilete && moldura.filete && faixas.alto[posicao].tipo === 'TEXTO',
      moldura,
      documento,
    });

  const pe = (posicao: 'esquerda' | 'centro' | 'direita', nome: string) =>
    caixa(nome, faixas.pe[posicao], { alto: false, filete: false, moldura, documento });

  return [
    alto('esquerda', 'top-left'),
    alto('centro', 'top-center'),
    alto('direita', 'top-right'),
    pe('esquerda', 'bottom-left'),
    pe('centro', 'bottom-center'),
    pe('direita', 'bottom-right'),
  ].join(' ');
}

/**
 * Uma caixa de margem.
 *
 * O filete sai nas caixas de texto do alto (ver `caixas`): embaixo do texto do
 * cabeçalho, ou nas duas laterais quando o cabeçalho é em duas pontas. Caixa
 * vazia recebe `content: none` — ela não é desenhada, então não há borda para
 * desenhar de qualquer forma.
 */
function caixa(
  nome: string,
  conteudo: CaixaDaFolha,
  opcoes: {
    alto: boolean;
    filete: boolean;
    moldura: MolduraDaFolha;
    documento: DocumentoAcademico;
    /** O fundo da imagem de cabeçalho (ver `fundoDaImagem` em `cssDaFolha`). */
    fundoDaImagem?: string;
  },
): string {
  const { alto, filete, moldura, documento, fundoDaImagem = '' } = opcoes;
  const vazia = conteudo.tipo === 'VAZIA';
  const regras: string[] = [];

  if (conteudo.tipo === 'NUMERO') regras.push('content: counter(page);');
  else if (!vazia) regras.push(`content: "${escaparTexto(conteudo.texto)}";`);
  /*
   * Caixa vazia com `content: none` **não é desenhada** — e com ela iria o fundo da
   * imagem de cabeçalho. Por isso a caixa que carrega a imagem nasce com `content: ""`:
   * vazia de texto, mas desenhada.
   */
  else regras.push(fundoDaImagem ? 'content: "";' : 'content: none;');

  if (fundoDaImagem) regras.push(fundoDaImagem.trim());

  if (!vazia) {
    regras.push(...regrasDoEstilo(moldura, documento));
  }

  if (nome.endsWith('-left')) regras.push('text-align: left;');
  else if (nome.endsWith('-right')) regras.push('text-align: right;');
  else regras.push('text-align: center;');

  regras.push(alto ? 'vertical-align: bottom;' : 'vertical-align: top;');

  if (filete) {
    regras.push(
      `border-bottom: ${ESPESSURA_DO_FILETE_MM}mm solid ${COR_DO_FILETE};`,
      'padding-bottom: 2mm;',
    );
  }

  return `@${nome} { ${regras.join(' ')} }`;
}

/** Nenhuma faixa: as folhas de abertura saem limpas. */
function folhaLimpa(): FaixasDaFolha {
  const vazia: CaixaDaFolha = { tipo: 'VAZIA' };
  return {
    alto: { esquerda: vazia, centro: vazia, direita: vazia },
    pe: { esquerda: vazia, centro: vazia, direita: vazia },
  };
}

/** Alguma caixa desta faixa tem conteúdo? */
function temCaixa(faixa: FaixasDaFolha['alto']): boolean {
  return [faixa.esquerda, faixa.centro, faixa.direita].some((item) => item.tipo !== 'VAZIA');
}

/**
 * As regras de texto da faixa, a partir do estilo que a pessoa escolheu.
 *
 * Tudo o que é do **estilo** mora aqui e vale para a caixa inteira: a margem da
 * folha aceita texto puro, então negrito no meio da frase não existe ali. O que
 * existe — e sai no papel — é negrito, itálico, sublinhado, versalete, caixa
 * alta, corpo, cor, espaçamento entre letras e família, aplicados à faixa toda,
 * como o estilo de um parágrafo no Word.
 */
function regrasDoEstilo(moldura: MolduraDaFolha, documento: DocumentoAcademico): string[] {
  const estilo = moldura.estilo;
  const regras = [
    `font-family: ${familiaDaFaixa(estilo, documento)};`,
    `font-size: ${moldura.corpoPt}pt;`,
    `color: ${estilo.cor};`,
  ];

  if (estilo.negrito) regras.push('font-weight: 700;');
  if (estilo.italico) regras.push('font-style: italic;');
  if (estilo.sublinhado) regras.push('text-decoration: underline;');
  /*
   * Caixa alta e versalete juntos não fazem sentido: em maiúsculas o versalete
   * não tem o que encolher. A caixa alta manda.
   */
  if (estilo.caixaAlta) regras.push('text-transform: uppercase;');
  else if (estilo.versalete) regras.push('font-variant: small-caps;');
  if (estilo.espacamento > 0) regras.push(`letter-spacing: ${(estilo.espacamento / 100).toFixed(2)}em;`);

  /* `pre-line` é o que faz o `\A` da string virar quebra de linha de verdade. */
  regras.push('white-space: pre-line;');

  return regras;
}

/**
 * O estilo da faixa em CSS de tela, para a prévia mostrar o mesmo que vai sair.
 *
 * A prévia da faixa (na tela e no editor) precisa das mesmas decisões da
 * impressão — inclusive a escolha entre caixa alta e versalete.
 */
export function estiloDaFaixaEmCss(documento: DocumentoAcademico): CSSProperties {
  const moldura = molduraDaFolha(documento);
  const estilo = moldura.estilo;

  return {
    fontFamily: familiaDaFaixa(estilo, documento),
    fontSize: `${moldura.corpoPt}pt`,
    color: estilo.cor,
    fontWeight: estilo.negrito ? 700 : 400,
    fontStyle: estilo.italico ? 'italic' : 'normal',
    textDecoration: estilo.sublinhado ? 'underline' : 'none',
    textTransform: estilo.caixaAlta ? 'uppercase' : 'none',
    fontVariant: !estilo.caixaAlta && estilo.versalete ? 'small-caps' : 'normal',
    letterSpacing: estilo.espacamento > 0 ? `${(estilo.espacamento / 100).toFixed(2)}em` : 'normal',
    whiteSpace: 'pre-line',
  };
}

/**
 * O texto entra num `content: "..."` de CSS.
 *
 * Aspas e barras invertidas fechariam a string e derrubariam as regras seguintes
 * — foi assim que uma folha de estilo inteira deixou de valer numa medição (e as
 * páginas pararam de quebrar, o que confundiu a conclusão). O resto do texto
 * fica como está, e a quebra de linha vira `\A`, que é como uma string de CSS
 * ganha uma linha nova.
 */
function escaparTexto(texto: string): string {
  return texto
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\r?\n/g, '\\A ')
    .trim();
}
